import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import coachingData from '../data/coaching.json';

export default function Navigation() {
  const { user, logout } = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const { name, tagline, logo } = coachingData;
  const displayName = name || 'Savvy Academy';
  const displayTagline = tagline || 'JEE · NEET · Olympiad';
  const logoSrc = logo || '/savvy-logo-standard-512.png';
  const isSignupPage = location.pathname === '/signup-mobile';
  const isLoginPage = location.pathname === '/login-mobile';
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profile = user?.user || user || {};
  const profileName = profile.full_name || profile.name || profile.email || '';
  const profileEmail = profile.email || 'Email not provided';
  const profilePhoto = profile.photo || profile.avatar || '';
  const firstName = profileName ? String(profileName).trim().split(/\s+/)[0] : '';
  const initials = profileName
    ? profileName
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('')
    : 'SA';

  const dispatchMenuState = (open) => {
    if (typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('nav-menu-open', { detail: { open } }));
  };

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleEnrollFormOpen = () => {
      setMobileMenuOpen((prev) => {
        if (prev && typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('nav-menu-open', { detail: { open: false } }));
        }
        return false;
      });
    };

    window.addEventListener('enrol-form-open', handleEnrollFormOpen);
    return () => window.removeEventListener('enrol-form-open', handleEnrollFormOpen);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => {
      const next = !prev;
      dispatchMenuState(next);
      return next;
    });
  };
  const closeMobileMenu = () => {
    setMobileMenuOpen((prev) => {
      if (prev) {
        dispatchMenuState(false);
      }
      return false;
    });
  };

  const handleLogout = () => {
    if (typeof logout === 'function') {
      logout();
    }
    navigate('/login-mobile');
    closeMobileMenu();
  };

  const underlineNavLinkClass = 'relative text-text-secondary hover:text-primary transition-colors after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:scale-x-0 hover:after:scale-x-100 after:bg-primary after:origin-left after:transition-transform after:duration-300';

  return (
    <>
      <header className="bg-surface shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <Link
                to={user ? '/home' : '/'}
                onClick={closeMobileMenu}
                className="flex-shrink-0"
              >
                <img src={logoSrc} alt={displayName} className="h-12 w-12 md:h-16 md:w-16 object-contain" />
              </Link>
              <Link
                to={user ? '/home' : '/'}
                onClick={closeMobileMenu}
                className="flex flex-col leading-tight"
              >
                <span className="font-extrabold text-primary text-lg leading-tight md:text-2xl">{displayName}</span>
                <span className="text-text-primary text-xs tracking-[0.25em] uppercase md:text-[11px] md:tracking-[0.3em]">{displayTagline}</span>
              </Link>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2 md:hidden">
              <button
                type="button"
                onClick={toggleMobileMenu}
                className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border text-text-secondary hover:text-primary"
                aria-label="Toggle navigation menu"
              >
                <span className="sr-only">Menu</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  className="h-6 w-6"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
                </svg>
              </button>
            </div>

            <nav className="hidden md:flex md:items-center md:space-x-6">
              {!user && (
                <>
                  <Link to="/about" className={underlineNavLinkClass}>About Savvy</Link>
                  <Link to="/courses" className={underlineNavLinkClass}>Courses</Link>
                </>
              )}
              {user ? (
                <>
                  <Link
                    to="/home"
                    className={underlineNavLinkClass}
                  >
                    Home
                  </Link>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 rounded-full border border-border px-2 py-1 text-sm text-text-secondary hover:text-primary"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold overflow-hidden">
                      {profilePhoto ? (
                        <img src={profilePhoto} alt={profileName || 'Profile'} className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        initials
                      )}
                    </span>
                    <span className="font-medium hidden lg:inline">{firstName || 'Profile'}</span>
                  </Link>
                  <button onClick={handleLogout} className="btn-secondary">Logout</button>
                </>
              ) : (
                <>
                  {!isSignupPage && (
                    <Link to="/signup-mobile" className="btn-primary inline-flex items-center justify-center min-w-[120px]">Sign Up</Link>
                  )}
                  {!isLoginPage && (
                    <Link to="/login-mobile" className="btn-primary inline-flex items-center justify-center min-w-[120px]">Login</Link>
                  )}
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 md:hidden transition-opacity ${
          isMobileMenuOpen ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'
        }`}
        aria-hidden={isMobileMenuOpen ? 'false' : 'true'}
      >
        <div
          className={`absolute inset-0 bg-black/40 transition-opacity duration-200 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeMobileMenu}
        ></div>
        <aside
          className={`absolute right-0 top-0 h-full w-72 max-w-[85vw] transform bg-surface shadow-xl transition-transform duration-200 ${
            isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <div className="flex items-center gap-3 overflow-hidden">
              <img src={logoSrc} alt={displayName} className="h-10 w-10 flex-shrink-0 object-contain" />
              <span className="font-semibold text-primary text-sm leading-tight truncate max-w-[150px]">
                {displayName}
              </span>
            </div>
            <button
              type="button"
              onClick={closeMobileMenu}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary hover:bg-primary/20"
              aria-label="Close menu"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="h-5 w-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="px-5 py-6 space-y-4">
            {!user && (
              <>
                <Link to="/about" onClick={closeMobileMenu} className="block text-lg font-medium text-text-secondary hover:text-primary">About Savvy</Link>
                <Link to="/courses" onClick={closeMobileMenu} className="block text-lg font-medium text-text-secondary hover:text-primary">Courses</Link>
              </>
            )}
            {user ? (
              <div className="space-y-4">
                <Link
                  to="/home"
                  onClick={closeMobileMenu}
                  className="block text-lg font-medium text-text-secondary hover:text-primary"
                >
                  Home
                </Link>
                <Link
                  to="/profile"
                  onClick={closeMobileMenu}
                  className="flex items-center gap-3 rounded-full border border-border px-3 py-2 text-text-secondary hover:text-primary"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-semibold overflow-hidden">
                    {profilePhoto ? (
                      <img src={profilePhoto} alt={profileName || 'Profile'} className="h-full w-full object-cover" />
                    ) : (
                      initials
                    )}
                  </span>
                  <span className="block font-semibold text-sm text-primary truncate">{firstName || 'Profile'}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full btn-primary rounded-full px-4 py-2 text-center"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {!isSignupPage && (
                  <Link
                    to="/signup-mobile"
                    onClick={closeMobileMenu}
                    className="block rounded-lg bg-primary px-4 py-2 text-center font-semibold text-white"
                  >
                    Sign Up
                  </Link>
                )}
                {!isLoginPage && (
                  <Link
                    to="/login-mobile"
                    onClick={closeMobileMenu}
                    className="block rounded-lg border border-primary px-4 py-2 text-center font-semibold text-primary"
                  >
                    Login
                  </Link>
                )}
              </div>
            )}
          </nav>
        </aside>
      </div>
    </>
  );
}
